import { useMemo } from 'react';

import { useAppSelector } from 'store/hooks';

import {
  getActiveFile,
  getIsLegacyDxfViewerForPartFile,
} from '../data/reducer';
import {
  VIEWER_KINDS,
  type ViewerKind,
  resolveViewerKind,
  isDxfSceneEligible,
} from '../resolveViewerKind';

/**
 * Which viewer renders the active file. The single source of truth — every
 * "is this a PDF / is this 3D / should the CAD viewer mount" question in the app
 * should be answered from this, not by re-deriving from filename or `hasGeometry`.
 * See `resolveViewerKind` for the precedence rules.
 *
 * This is a hook rather than a selector because it combines the active file with
 * the per-file legacy override.
 */
export const useActiveViewerKind = (): ViewerKind => {
  const activeFile = useAppSelector(getActiveFile);
  const isLegacyDxfViewerForced = useAppSelector((state) =>
    getIsLegacyDxfViewerForPartFile(state, activeFile?.uuid),
  );

  return useMemo(
    () => resolveViewerKind(activeFile, { isLegacyDxfViewerForced }),
    [activeFile, isLegacyDxfViewerForced],
  );
};

/**
 * True when the active file could render in the new DXF scene viewer, ignoring
 * any per-file "Legacy DXF Viewer" override. Gates whether that toggle should be
 * offered at all from the CAD viewer's legacy 2D mode — a file that never had a
 * scene to begin with has nothing to switch back to.
 */
export const useIsActiveFileDxfSceneEligible = (): boolean => {
  const activeFile = useAppSelector(getActiveFile);

  return useMemo(
    () => Boolean(activeFile && isDxfSceneEligible(activeFile)),
    [activeFile],
  );
};

/** True when the PDF viewer is the active viewer for this file. */
export const useIsActiveFilePDFCompatible = (): boolean =>
  useActiveViewerKind().kind === VIEWER_KINDS.PDF;

/**
 * True when the CAD viewer is the active viewer — i.e. gates the CAD toolbars,
 * measurement displays, and interrogation panels.
 *
 * NB: this is false for DXF/DWG files rendered by the new DXF scene viewer,
 * which is intended — those files must not show CAD tooling. Legacy DXFs still
 * resolve to MODEL and behave exactly as before.
 */
export const useIsActiveFile3DRenderable = (): boolean =>
  useActiveViewerKind().kind === VIEWER_KINDS.MODEL;
